import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { FriendsService } from '../../services/FriendsService';
import { PublicProfileService } from '../../services/PublicProfileService';

function initials(name) {
  const src = (name || '?').trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function FriendsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [myCode, setMyCode] = useState('');
  const [friends, setFriends] = useState(null);
  const [requests, setRequests] = useState([]);
  const [codeInput, setCodeInput] = useState('');
  const [status, setStatus] = useState(null); // { type: 'error'|'success', message }
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const [list, incoming] = await Promise.all([
      FriendsService.getFriends(),
      FriendsService.getIncomingRequests(),
    ]);
    setFriends(list);
    setRequests(incoming);
  }

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let active = true;
    PublicProfileService.getOrCreateInviteCode().then((c) => { if (active) setMyCode(c); });
    refresh().catch(() => { if (active) setFriends([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  async function handleAddFriend(e) {
    e.preventDefault();
    if (!codeInput.trim()) return;
    setBusy(true);
    setStatus(null);
    try {
      const result = await FriendsService.sendRequestByCode(codeInput);
      if (result.ok) {
        setStatus({ type: 'success', message: t('friends.requestSent', { name: result.target.displayName || t('profile.learner') }) });
        setCodeInput('');
      } else if (result.reason === 'self') {
        setStatus({ type: 'error', message: t('friends.cannotAddSelf') });
      } else if (result.reason === 'already_friends') {
        setStatus({ type: 'error', message: t('friends.alreadyFriends') });
      } else {
        setStatus({ type: 'error', message: t('friends.codeNotFound') });
      }
    } catch {
      setStatus({ type: 'error', message: t('friends.genericError') });
    }
    setBusy(false);
  }

  async function handleAccept(fromUid) {
    setBusy(true);
    try {
      await FriendsService.acceptRequest(fromUid);
      await refresh();
    } catch {
      setStatus({ type: 'error', message: t('friends.genericError') });
    }
    setBusy(false);
  }

  async function handleDecline(fromUid) {
    setBusy(true);
    try {
      await FriendsService.declineRequest(fromUid);
      await refresh();
    } catch {
      setStatus({ type: 'error', message: t('friends.genericError') });
    }
    setBusy(false);
  }

  async function handleShareCode() {
    const shareText = t('friends.shareMessage', { code: myCode });
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); return; } catch { /* user cancelled or unsupported */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setStatus({ type: 'success', message: t('friends.codeCopied') });
    } catch { /* clipboard unavailable — code is still visible on screen */ }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
        <div className="mx-auto max-w-lg space-y-5 text-center">
          <span className="text-5xl">👥</span>
          <h1 className="text-2xl font-black text-slate-900">{t('friends.title')}</h1>
          <p className="text-sm text-slate-500">{t('friends.signInNotice')}</p>
          <button
            onClick={() => navigate('/login')}
            className="mx-auto mt-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-sm font-bold text-white shadow-md"
          >
            {t('profile.loginArrow')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-lg space-y-5">
        <button
          onClick={() => navigate('/progress')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t('profile.backToHome')}
        </button>

        <h1 className="text-2xl font-black text-slate-900">{t('friends.title')}</h1>

        {/* My invite code */}
        <div className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('friends.yourCode')}</p>
          <div className="mt-2 flex items-center gap-3">
            <span className="rounded-xl bg-indigo-50 px-4 py-2 font-mono text-xl font-black tracking-widest text-indigo-700">
              {myCode || '······'}
            </span>
            <button
              onClick={handleShareCode}
              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
            >
              {t('friends.share')}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-400">{t('friends.codeHint')}</p>
        </div>

        {/* Add friend */}
        <form onSubmit={handleAddFriend} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">{t('friends.addFriend')}</p>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder={t('friends.enterCode')}
              className="min-w-0 flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm uppercase tracking-widest focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <button
              type="submit"
              disabled={busy}
              className="shrink-0 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-50"
            >
              {t('friends.send')}
            </button>
          </div>
          {status && (
            <p className={`mt-2 text-xs font-semibold ${status.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`}>
              {status.message}
            </p>
          )}
        </form>

        {/* Incoming requests */}
        {requests.length > 0 && (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-slate-400">{t('friends.requests')}</p>
            <ul className="space-y-2">
              {requests.map((req) => (
                <li key={req.id} className="flex items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-black text-white">
                    {initials(req.fromName)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{req.fromName || t('profile.learner')}</span>
                  <button onClick={() => handleAccept(req.id)} disabled={busy} className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700 disabled:opacity-50">
                    {t('friends.accept')}
                  </button>
                  <button onClick={() => handleDecline(req.id)} disabled={busy} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-500 disabled:opacity-50">
                    {t('friends.decline')}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Friends list */}
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <p className="p-6 pb-3 text-xs font-black uppercase tracking-widest text-slate-400">{t('friends.yourFriends')}</p>
          {friends === null && (
            <div className="space-y-2 px-6 pb-6">
              {Array.from({ length: 3 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="h-12 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          )}
          {friends !== null && friends.length === 0 && (
            <p className="px-6 pb-6 text-sm text-slate-500">{t('friends.noFriendsYet')}</p>
          )}
          {friends !== null && friends.length > 0 && (
            <ul>
              {friends.map((friend) => (
                <li key={friend.uid} className="flex items-center gap-3 border-t border-slate-100 px-6 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-black text-white">
                    {initials(friend.displayName)}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-slate-800">{friend.displayName || t('profile.learner')}</span>
                  {friend.streak > 0 && <span className="shrink-0 text-xs font-bold text-slate-400">🔥 {friend.streak}</span>}
                  <span className="shrink-0 text-xs font-bold text-indigo-600">{friend.weeklyXp || 0} XP</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
